import {
  Address,
  AmountFromBigInt,
  ChainPublicClientService,
  MarketAssetUserState,
  ProtocolKeyService,
} from "@silo/types";
import { Duration, Effect, pipe } from "effect";
import { getTokenBalance } from "./get-token-balance";
import { erc20Abi, getContract } from "viem";
import { siloLensAddress } from "@silo/contract-addresses";
import { siloLensAbi } from "@silo/contract-abi-types";
import { ContractsClientError } from "./types";
import { getCurrentTimestamp } from "@silo/utils";
import { Schema } from "@effect/schema";

export type GetMarketAssetUserState = ReturnType<
  typeof getMarketAssetUserState
>;
export type GetMarketAssetUserStateSuccess =
  Effect.Effect.Success<GetMarketAssetUserState>;
export type GetMarketAssetUserStateError =
  Effect.Effect.Error<GetMarketAssetUserState>;
export type GetMarketAssetUserStateContext =
  Effect.Effect.Context<GetMarketAssetUserState>;

interface GetMarketAssetUserStateParams {
  user: Address;
  marketAddress: Address;
  tokenAddress: Address;
  effectiveTokenAddress: Address;
  protectedShareToken: Address;
}

/**
 * TODO: could potentially save some RPC calls downstream by splitting this into two functions.
 *
 * The first would fetch the collateral, protected, and debt balances.
 *
 * The second would fetch those in addition to tokenBalance & projected debt balance.
 */

export function getMarketAssetUserState({
  user,
  marketAddress,
  tokenAddress,
  effectiveTokenAddress,
  protectedShareToken,
}: GetMarketAssetUserStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const currentTimestamp = getCurrentTimestamp();
    const projectionTimestamp = getProjectionTimestamp(currentTimestamp);

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const protectedShareTokenContract = getContract({
      address: protectedShareToken,
      abi: erc20Abi,
      client: publicClient,
    });

    const [tokenBalance, viemMarketAssetUserStateEncoded] = yield* Effect.all(
      [
        getTokenBalance(effectiveTokenAddress, user),

        Effect.tryPromise({
          try: async () => {
            const [
              collateralBalance,
              protectedBalance,
              debtBalance,
              projectedDebtBalance,
            ] = await Promise.all([
              siloLensContract.read.getDepositAmount([
                marketAddress,
                tokenAddress,
                user,
                currentTimestamp,
              ]),
              protectedShareTokenContract.read.balanceOf([user]),
              siloLensContract.read.getBorrowAmount([
                marketAddress,
                tokenAddress,
                user,
                currentTimestamp,
              ]),
              siloLensContract.read.getBorrowAmount([
                marketAddress,
                tokenAddress,
                user,
                projectionTimestamp,
              ]),
            ]);

            const encoded: ViemMarketAssetUserStateEncoded = {
              collateralBalance,
              protectedBalance,
              debtBalance,
              projectedDebtBalance,
            };

            return encoded;
          },
          catch: (cause) => new ContractsClientError({ cause }),
        }),
      ],
      { concurrency: "unbounded" }
    );

    const viemMarketAssetUserState = yield* pipe(
      viemMarketAssetUserStateEncoded,
      Schema.decode(ViemMarketAssetUserState)
    );

    const marketAssetUserState: MarketAssetUserState = {
      tokenBalance,
      ...viemMarketAssetUserState,
    };

    return marketAssetUserState;
  });
}

function getProjectionTimestamp(currentTimestamp: bigint): bigint {
  return pipe(
    currentTimestamp,
    Number,
    Duration.seconds,
    Duration.sum(Duration.minutes(2)),
    Duration.toSeconds,
    BigInt
  );
}

export type ViemMarketAssetUserState = typeof ViemMarketAssetUserState.Type;
export type ViemMarketAssetUserStateEncoded =
  typeof ViemMarketAssetUserState.Encoded;

export const ViemMarketAssetUserState = Schema.Struct({
  collateralBalance: AmountFromBigInt,
  protectedBalance: AmountFromBigInt,
  debtBalance: AmountFromBigInt,
  projectedDebtBalance: AmountFromBigInt,
}).annotations({ identifier: "ViemMarketAssetUserState" });
